/**
 * useAccountBurnRate — how fast is this organization actually spending?
 *
 * WHY (Miles, 2026-09-11, after INCIDENT 2026-09-10). The top-up ladder was a
 * fixed $100 / $250 / $500 / $1,000 with $500 pre-selected, and auto top-up
 * defaulted to $500 whenever the balance fell below $25. His ruling: the
 * suggested options should "center around timeline moving aggregate strategic
 * averages based on usage (few days) (one week) (2 weeks) (1 month), making
 * their options calculated, easy and personalized just for the organization's
 * usage".
 *
 * SOURCE. `accounts/{accountId}/account_usage/{YYYY-MM-DD}` — the per-day
 * `amountUsd` rollups the debit chokepoint already writes. Deliberately NOT
 * the raw expense ledger: one account wrote 625 expense rows in eight days,
 * so a 30-day window there is thousands of document reads every time the modal
 * opens. This is ~30.
 *
 * SCOPE. The ORGANIZATION's usage, per the ruling — read through the account
 * the user is acting in (`users/{uid}/account_membership/main`), which is the
 * same pointer the wallet display resolver uses. Personal-plane users simply
 * have no account and fall back to the flat ladder.
 *
 * The arithmetic lives in ../utils/burnRate, a twin of the server's SSOT that
 * a parity test pins. This hook only fetches and gap-fills.
 */
import { useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, query, where, documentId } from 'firebase/firestore';
import db from '../utils/firestore';
import {
  accountIdFromMembership,
  computeBurnRate,
  dailySpendWindowKeys,
  selectDailySpendRows,
  BurnRate,
} from '../utils/burnRate';

export interface UseAccountBurnRateResult {
  burn: BurnRate | null;
  isLoading: boolean;
}

export function useAccountBurnRate(uid: string | null | undefined): UseAccountBurnRateResult {
  const [burn, setBurn] = useState<BurnRate | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(uid));

  useEffect(() => {
    let alive = true;
    if (!uid) {
      setBurn(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    (async () => {
      try {
        const memberSnap = await getDoc(doc(db, `users/${uid}/account_membership/main`));
        const accountId = accountIdFromMembership(memberSnap.exists() ? memberSnap.data() : null);
        if (!accountId) {
          // Personal plane: no organization pace to personalise against.
          if (alive) { setBurn(null); setIsLoading(false); }
          return;
        }

        // Bounded by document id, so this is a key range — no index needed and
        // no rollup outside the window is ever read.
        const keys = dailySpendWindowKeys(Date.now());
        const oldest = keys[keys.length - 1];
        const newest = keys[0];

        const snap = await getDocs(query(
          collection(db, `accounts/${accountId}/account_usage`),
          where(documentId(), '>=', oldest),
          where(documentId(), '<=', newest),
        ));

        // The monthly-rollup exclusion and the zero gap-fill live in the shared
        // engine, so this page and the pre-charge warning read one series.
        const rows = snap.docs.map((d) => ({
          id: d.id,
          amountUsd: (d.data() as Record<string, unknown>).amountUsd,
        }));

        if (alive) { setBurn(computeBurnRate(selectDailySpendRows(rows, keys))); setIsLoading(false); }
      } catch {
        // Never block a purchase on this: no burn simply means the flat
        // fallback ladder, which is what every user saw before today.
        if (alive) { setBurn(null); setIsLoading(false); }
      }
    })();

    return () => { alive = false; };
  }, [uid]);

  return { burn, isLoading };
}
